import { AnimationBuilder, AnimationFactory, animate, keyframes, style } from '@angular/animations';

import { SlideComponent } from '../slide.component';
import { BaseSlideBehavior } from './base-slide-behavior';

interface TreeSlideAnimation {
    enterLeft: AnimationFactory;
    enterRight: AnimationFactory;
    leftCenter: AnimationFactory;
    centerRight: AnimationFactory;
    centerLeft: AnimationFactory;
    rightCenter: AnimationFactory;
    exitRight: AnimationFactory;
    exitLeft: AnimationFactory;
    staticExitLeft: AnimationFactory;
}

export class ThreeSlideBehavior extends BaseSlideBehavior {
    private slideToDuration = 100;
    constructor(slides: SlideComponent[], animationBuilder: AnimationBuilder, initialSlideIndex: number) {
        super(slides, animationBuilder, initialSlideIndex);

        const animations = this.getAnimations();

        this.slides.forEach((s) => s.animate(animations.staticExitLeft));

        this.slides[this.currentSlideIndex].animate(animations.rightCenter);
        this.slides[this.nextSlideIndex()].animate(animations.enterRight);
        this.slides[this.previousSlideIndex()].animate(animations.centerLeft);
    }

    slideRight(): void {
        const animations = this.getAnimations();

        this.slides[this.nextSlideIndex()].animate(animations.exitRight);

        this.currentSlideIndex = this.previousSlideIndex();

        this.slides[this.currentSlideIndex].animate(animations.leftCenter);
        this.slides[this.nextSlideIndex()].animate(animations.centerRight);
        this.slides[this.previousSlideIndex()].animate(animations.enterLeft);

        this.emitSlideChanged();
    }

    slideLeft(): void {
        const animations = this.getAnimations();

        this.slides[this.previousSlideIndex()].animate(animations.exitLeft);

        this.currentSlideIndex = this.nextSlideIndex();

        this.slides[this.currentSlideIndex].animate(animations.rightCenter);
        this.slides[this.nextSlideIndex()].animate(animations.enterRight);
        this.slides[this.previousSlideIndex()].animate(animations.centerLeft);

        this.emitSlideChanged();
    }

    slideTo(slideIndex: number): void {
        // eslint-disable-next-line no-param-reassign
        slideIndex = this.limitNumber(slideIndex, 0, this.maxSlideIndex);
        if (slideIndex === this.currentSlideIndex) {
            return;
        }

        if (slideIndex < this.currentSlideIndex) {
            this.slideRight();
        } else {
            this.slideLeft();
        }
        setTimeout(() => {
            this.slideTo(slideIndex);
        }, this.slideToDuration);
    }

    get canSlideLeft(): boolean {
        return this.currentSlideIndex < this.maxSlideIndex;
    }

    get canSlideRight(): boolean {
        return this.currentSlideIndex > 0;
    }

    private getAnimations(): TreeSlideAnimation {
        const enterLeft = this.animationBuilder.build([
            animate(
                this.animationDuration,
                keyframes([style({ transform: 'translateX(-150%)', opacity: 0.095 }), style({ transform: 'translateX(-100%)', opacity: 0.095 })]),
            ),
        ]);

        const leftCenter = this.animationBuilder.build([
            animate(
                this.animationDuration,
                keyframes([style({ transform: 'translateX(-100%)', opacity: 0.095 }), style({ transform: 'translateX(0%)', opacity: 1 })]),
            ),
        ]);

        const centerLeft = this.animationBuilder.build([
            animate(
                this.animationDuration,
                keyframes([style({ transform: 'translateX(0%)', opacity: 1 }), style({ transform: 'translateX(-100%)', opacity: 0.095 })]),
            ),
        ]);

        const exitLeft = this.animationBuilder.build([
            animate(
                this.animationDuration,
                keyframes([style({ transform: 'translateX(-100%)', opacity: 0.095 }), style({ transform: 'translateX(-500%)', opacity: 0.095 })]),
            ),
        ]);

        const enterRight = this.animationBuilder.build([
            animate(
                this.animationDuration,
                keyframes([style({ transform: 'translateX(150%)', opacity: 0.095 }), style({ transform: 'translateX(100%)', opacity: 0.095 })]),
            ),
        ]);

        const rightCenter = this.animationBuilder.build([
            animate(
                this.animationDuration,
                keyframes([style({ transform: 'translateX(100%)', opacity: 0.095 }), style({ transform: 'translateX(0%)', opacity: 1 })]),
            ),
        ]);

        const centerRight = this.animationBuilder.build([
            animate(
                this.animationDuration,
                keyframes([style({ transform: 'translateX(0%)', opacity: 1 }), style({ transform: 'translateX(100%)', opacity: 0.095 })]),
            ),
        ]);

        const exitRight = this.animationBuilder.build([
            animate(
                this.animationDuration,
                keyframes([style({ transform: 'translateX(100%)', opacity: 0.095 }), style({ transform: 'translateX(300%)', opacity: 0.095 })]),
            ),
        ]);

        const staticExitLeft = this.animationBuilder.build([style({ transform: 'translateX(-500%)', opacity: 0.095 })]);

        return {
            enterLeft,
            enterRight,
            leftCenter,
            centerRight,
            centerLeft,
            rightCenter,
            exitRight,
            exitLeft,
            staticExitLeft,
        };
    }
}
